import { RESULT_RANK_MAX } from "./results";
import { TIMING_REACTION_MAX_MS, TIMING_TIME_MAX_MS, type TimingLaneStatus } from "./timing";
import { TIMING_BENCH_WIND_MAX_MPS, type TimingBenchLane, type TimingBenchSnapshot } from "./timing-bench";

/**
 * Athletics track timer → the timing bench relay (`timing-bench.ts`), the
 * same path as the pool timer: a venue bridge reads the timer on the LAN,
 * converts it here and PUTs the snapshot; OVR shows it beside a track race and
 * the operator's "Apply timer results" is the one writer.
 *
 * Two reads of the timer's console make one snapshot:
 *
 *   GET /api/athletics/running-time  — the RESULTS console: per event and
 *        heat, each lane's official place, time (ms, already rounded up to the
 *        hundredth) and DNS / DNF / DQ, the wind, `finished`. The source of
 *        every time sent.
 *   GET /api/feed/live               — the race on the start system now: its
 *        event / heat, distance, and each lane's reaction time. Only this
 *        race's reactions are used, and only when it is the same heat.
 *
 * The live feed's `estimated` fields (position, gap, split, speed, track x / y)
 * are the simulator's drawing of the race, not timing: never read.
 *
 * Track lanes are the start list's lanes, so `timer_lane` = `pool_lane` =
 * the lane. Nothing is guessed: a lane the console has not timed sends no
 * finish; a status it does not know is left out and named in `warnings`.
 */

/** One lane of a heat on the results console. */
export interface AthleticsRtLane {
	place?: number | null;
	lane?: number | null;
	bib?: string | null;
	time?: string | null;
	totalMs?: number | null;
	/** DNS / DNF / DQ, else null. */
	status?: string | null;
	/** The console thinks the lane did not start (no reaction, no finish) — a hint, not a verdict. */
	presumedScratched?: boolean | null;
}

/** One heat on the results console. `event` / `heat` are text there. */
export interface AthleticsRtEvent {
	event?: string | number | null;
	heat?: string | number | null;
	/** "+0.6", "nwi" (no wind information). */
	wind?: string | null;
	windMs?: number | null;
	finished?: boolean | null;
	updatedAt?: number | null;
	lanes?: AthleticsRtLane[] | null;
}

/** `GET /api/athletics/running-time`. */
export interface AthleticsRunningTime {
	running?: boolean | null;
	clock?: { totalMs?: number | null } | null;
	events?: AthleticsRtEvent[] | null;
}

/** `GET /api/feed/live` — only the fields read. */
export interface AthleticsLiveFeed {
	simulated?: boolean | null;
	event?: { no?: number | string | null; heat?: number | string | null; name?: string | null; distance?: number | null } | null;
	athletes?: { lane?: number | null; reaction?: string | null }[] | null;
}

/** The bench system name: the simulator, or a real results console. */
export const ATHLETICS_TIMER_SYSTEMS = { simulated: "ATHLETICS_SIM", live: "ATHLETICS_TIMER" } as const;

const STATUS: Readonly<Record<string, TimingLaneStatus>> = { DNS: "DNS", DNF: "DNF", DQ: "DSQ", DSQ: "DSQ" };

const intIn = (v: unknown, min: number, max: number): number | null => (typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : null);
const key = (v: unknown): string | null => (typeof v === "number" && Number.isFinite(v) ? String(v) : typeof v === "string" && v.trim() !== "" ? v.trim() : null);

/** "0.206" → 206; anything that is not a reading (blank, "-", out of range) → null. */
export function athleticsReactionMs(text: string | null | undefined): number | null {
	if (typeof text !== "string" || !/^\d{1,2}(\.\d{1,3})?$/.test(text.trim())) return null;
	return intIn(Math.round(Number(text.trim()) * 1000), 0, TIMING_REACTION_MAX_MS);
}

/**
 * The heat the snapshot is about: the live race's event + heat when the
 * results console has it, else the console's most recently updated heat.
 */
function pickHeat(rt: AthleticsRunningTime, live: AthleticsLiveFeed | null): { heat: AthleticsRtEvent; isLive: boolean } | null {
	const heats = (rt.events ?? []).filter((e) => e && Array.isArray(e.lanes));
	if (heats.length === 0) return null;
	const ev = key(live?.event?.no);
	const ht = key(live?.event?.heat);
	if (ev !== null && ht !== null) {
		const hit = heats.find((e) => key(e.event) === ev && key(e.heat) === ht);
		if (hit) return { heat: hit, isLive: true };
	}
	const latest = heats.reduce((a, b) => ((b.updatedAt ?? 0) > (a.updatedAt ?? 0) ? b : a));
	return { heat: latest, isLive: false };
}

/**
 * The results console (+ the live race) → one bench snapshot, kind TRACK.
 * Pure. `capturedAt` is when the bridge read it (ISO 8601 with an offset).
 *
 * - **Time**: the console's `totalMs` (the hundredth it prints) as a one-leg
 *   final reading; no time → no reading.
 * - **Place** only with a time. **Status**: DNS / DNF / DQ → DSQ.
 * - **Reaction**: from the live feed, only when it is this heat.
 * - **Wind**: `windMs` (null for "nwi"); out of the gauge's range → left out, warned.
 * - **Distance**: the live race's, when it is this heat (the meet's own wins on Apply).
 */
export function athleticsToBenchSnapshot(rt: AthleticsRunningTime, live: AthleticsLiveFeed | null, capturedAt: string): TimingBenchSnapshot {
	const simulated = live?.simulated === true;
	const warnings: string[] = [];
	const picked = pickHeat(rt, live);
	const clockMs = intIn(rt.clock?.totalMs, 0, TIMING_TIME_MAX_MS);
	const base: TimingBenchSnapshot = {
		kind: "TRACK",
		system: simulated ? ATHLETICS_TIMER_SYSTEMS.simulated : ATHLETICS_TIMER_SYSTEMS.live,
		simulated,
		captured_at: capturedAt,
		clock: clockMs === null ? null : { ms: clockMs, running: rt.running === true },
		resets: 0,
		warnings,
		pool_length_m: null,
		distance_m: null,
		relay_legs: null,
		lanes: [],
	};
	if (!picked) return base;
	const { heat, isLive } = picked;

	const reactionByLane = new Map<number, number>();
	if (isLive) {
		for (const a of live?.athletes ?? []) {
			const lane = intIn(a?.lane, 0, 99);
			const r = athleticsReactionMs(a?.reaction);
			if (lane !== null && r !== null) reactionByLane.set(lane, r);
		}
	}

	const lanes: TimingBenchLane[] = [];
	const seen = new Set<number>();
	for (const l of heat.lanes ?? []) {
		const lane = intIn(l?.lane, 0, 99);
		if (lane === null || seen.has(lane)) continue;
		seen.add(lane);
		const raw = typeof l.status === "string" ? l.status.trim().toUpperCase() : "";
		const status = raw === "" ? null : (STATUS[raw] ?? null);
		if (raw !== "" && status === null) warnings.push(`Lane ${lane}: status "${l.status}" is not one OVR knows — enter it by hand.`);
		if (l.presumedScratched === true && status === null) warnings.push(`Lane ${lane}: the console thinks this athlete did not start.`);
		const finish = status === "DNS" ? null : intIn(l.totalMs, 0, TIMING_TIME_MAX_MS);
		const reaction = status === "DNS" ? null : (reactionByLane.get(lane) ?? null);
		lanes.push({
			timer_lane: lane,
			pool_lane: lane,
			legs: finish === null ? [] : [{ leg: 1, final: true, to_ms: finish, splits: [] }],
			place: finish === null ? null : intIn(l.place, 1, RESULT_RANK_MAX),
			suspect: false,
			reasons: [],
			status,
			...(reaction !== null ? { reaction_ms: reaction } : {}),
		});
	}
	lanes.sort((a, b) => a.timer_lane - b.timer_lane);

	let wind: number | null = null;
	if (typeof heat.windMs === "number" && Number.isFinite(heat.windMs)) {
		if (Math.abs(heat.windMs) <= TIMING_BENCH_WIND_MAX_MPS) wind = Math.round(heat.windMs * 10) / 10;
		else warnings.push(`Wind ${heat.windMs} m/s is outside the gauge's range — not sent.`);
	}
	const distance = isLive && typeof live?.event?.distance === "number" && live.event.distance > 0 ? live.event.distance : null;
	const name = isLive && typeof live?.event?.name === "string" ? live.event.name.trim() : "";
	const label = [key(heat.event) && `Event ${key(heat.event)}`, key(heat.heat) && `Heat ${key(heat.heat)}`, name].filter(Boolean).join(" · ");

	return {
		...base,
		distance_m: distance,
		lanes,
		wind_mps: wind,
		...(label ? { race_label: label.slice(0, 120) } : {}),
	};
}
